/**
 * DV-STAGE-003: Input Source Picker Component
 * Allows selecting between pre-canned samples, last output, and custom JSON
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, Copy, AlertCircle, RefreshCw, Clock, Database } from 'lucide-react';
import { cn } from '@/utils/cn';
import { InputSourcePickerProps, StageInputSource } from '@/types/stage-tester';

// Re-export for backward compatibility
export type InputSource = StageInputSource;
export type { InputSourcePickerProps };

export function InputSourcePicker({
  stage,
  source,
  onSourceChange,
  samples,
  onSampleSelect,
  customJson,
  onCustomChange,
  lastOutputRef,
  onLastOutputRefChange: _onLastOutputRefChange,
  isLoadingSamples,
  samplesError,
  customJsonError,
  lastOutputError
}: InputSourcePickerProps) {
  const [expandedSample, setExpandedSample] = useState<string | null>(null);
  const [showCustomPreview, setShowCustomPreview] = useState(false);

  const handleSourceChange = (newSource: typeof source) => {
    onSourceChange(newSource);
  };

  const handleSampleClick = (sampleId: string) => {
    onSampleSelect(sampleId);
    if (expandedSample === sampleId) {
      setExpandedSample(null);
    } else {
      setExpandedSample(sampleId);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatJsonPreview = (obj: any): string => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return 'Invalid JSON';
    }
  };

  return (
    <div className="space-y-4">
      {/* Source Selection */}
      <div className="flex flex-col space-y-2">
        <label className="text-sm font-medium text-gray-700">Input Source</label>
        <div className="flex space-x-2">
          <button
            onClick={() => handleSourceChange('sample')}
            className={cn(
              'px-3 py-2 text-sm rounded-md border transition-colors',
              source === 'sample'
                ? 'bg-blue-50 border-blue-200 text-blue-900'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            )}
          >
            <Database className="w-4 h-4 inline mr-2" />
            Pre-canned
          </button>
          <button
            onClick={() => handleSourceChange('last_output')}
            className={cn(
              'px-3 py-2 text-sm rounded-md border transition-colors',
              source === 'last_output'
                ? 'bg-blue-50 border-blue-200 text-blue-900'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            )}
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Last Output
          </button>
          <button
            onClick={() => handleSourceChange('custom')}
            className={cn(
              'px-3 py-2 text-sm rounded-md border transition-colors',
              source === 'custom'
                ? 'bg-blue-50 border-blue-200 text-blue-900'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            )}
          >
            Custom JSON
          </button>
        </div>
      </div>

      {/* Pre-canned Samples */}
      {source === 'sample' && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Available Samples</label>

          {isLoadingSamples && (
            <div className="flex items-center text-sm text-gray-500">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              Loading samples...
            </div>
          )}

          {samplesError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex items-start">
                <AlertCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="text-red-700 font-medium">Failed to load samples</p>
                  <p className="text-red-600 mt-1">{samplesError}</p>
                </div>
              </div>
            </div>
          )}

          {!isLoadingSamples && !samplesError && samples.length === 0 && (
            <div className="text-sm text-gray-500 py-4 text-center border border-gray-200 rounded-md">
              No samples available for {stage}
            </div>
          )}

          {samples.length > 0 && (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {samples.map((sample) => (
                <div key={sample.id} className="border border-gray-200 rounded-md">
                  <button
                    onClick={() => handleSampleClick(sample.id)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900">{sample.label}</span>
                        {sample.isDefault && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                            Default
                          </span>
                        )}
                      </div>
                      {sample.summary && (
                        <p className="text-sm text-gray-500 mt-1">{sample.summary}</p>
                      )}
                      {sample.tags && sample.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {sample.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {expandedSample === sample.id ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>

                  {expandedSample === sample.id && (
                    <div className="border-t border-gray-200 p-3 bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Sample Input</span>
                        <button
                          onClick={() => copyToClipboard(formatJsonPreview(sample.input))}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Copy to clipboard"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <pre className="text-xs bg-white border border-gray-200 rounded p-2 overflow-x-auto max-h-32">
                        {formatJsonPreview(sample.input)}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Last Output */}
      {source === 'last_output' && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Last Output Reference</label>

          {lastOutputError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex items-start">
                <AlertCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="text-red-700 font-medium">Last output not available</p>
                  <p className="text-red-600 mt-1">{lastOutputError}</p>
                  <p className="text-red-600 mt-2">
                    Switch to "Pre-canned" or "Custom JSON" to provide input manually.
                  </p>
                </div>
              </div>
            </div>
          )}

          {lastOutputRef && !lastOutputError && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="text-sm">
                <div className="flex items-center text-blue-900 font-medium">
                  <Clock className="w-4 h-4 mr-2" />
                  {lastOutputRef.origin === 'session' ? 'Session Output' : 'Execution Output'}
                </div>
                <div className="mt-2 text-blue-800">
                  <div>Stage: <span className="font-mono">{lastOutputRef.stage}</span></div>
                  {lastOutputRef.executionId && (
                    <div>Execution: <span className="font-mono text-xs">{lastOutputRef.executionId}</span></div>
                  )}
                  {lastOutputRef.updatedAt && (
                    <div>Updated: {new Date(lastOutputRef.updatedAt).toLocaleString()}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!lastOutputRef && !lastOutputError && (
            <div className="text-sm text-gray-500 py-4 text-center border border-gray-200 rounded-md">
              No previous stage output available
            </div>
          )}
        </div>
      )}

      {/* Custom JSON */}
      {source === 'custom' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Custom JSON Input</label>
            <button
              onClick={() => setShowCustomPreview(!showCustomPreview)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {showCustomPreview ? 'Hide' : 'Show'} Preview
            </button>
          </div>

          <textarea
            value={customJson}
            onChange={(e) => onCustomChange(e.target.value)}
            placeholder="Enter JSON input for this stage..."
            className={cn(
              'w-full h-32 px-3 py-2 text-sm border rounded-md font-mono',
              customJsonError
                ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
            )}
          />

          {customJsonError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex items-start">
                <AlertCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="text-red-700 font-medium">Invalid JSON</p>
                  <p className="text-red-600 mt-1">{customJsonError}</p>
                </div>
              </div>
            </div>
          )}

          {showCustomPreview && customJson && !customJsonError && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Parsed JSON</span>
                <button
                  onClick={() => copyToClipboard(customJson)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title="Copy to clipboard"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <pre className="text-xs bg-white border border-gray-200 rounded p-2 overflow-x-auto max-h-32">
                {formatJsonPreview(JSON.parse(customJson))}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}